import React from 'react';
import { Loader2 } from 'lucide-react';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  brightSnow: '#FFFFFF',
};

type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';

interface LoadingSpinnerProps {
  /** Size of the spinner: sm (16px), md (32px), lg (48px), xl (64px) */
  size?: SpinnerSize;
  /** Optional text to display below the spinner */
  text?: string;
  /** Whether to display as a full-page centered loader */
  fullPage?: boolean;
  /** Custom className for the container */
  className?: string;
}

const sizeClasses: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

const textSizeClasses: Record<SpinnerSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-lg font-medium',
  xl: 'text-xl font-medium',
};

/**
 * Standardized loading spinner component for BrandComply
 * 
 * Usage:
 * - Inline/button: <LoadingSpinner size="sm" />
 * - Section loader: <LoadingSpinner size="md" text="Loading..." />
 * - Full page: <LoadingSpinner size="lg" text="Loading campaign..." fullPage />
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'lg',
  text,
  fullPage = false,
  className = '',
}) => {
  const spinner = (
    <div className={`text-center ${className}`}>
      <Loader2 
        className={`${sizeClasses[size]} animate-spin mx-auto ${text ? 'mb-4' : ''}`} 
        style={{ color: PALETTE.inchworm }} 
      />
      {text && (
        <p 
          className={textSizeClasses[size]} 
          style={{ color: PALETTE.gunmetal }}
        >
          {text}
        </p>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center" 
        style={{ background: PALETTE.brightSnow }}
      >
        {spinner}
      </div>
    );
  }

  return spinner;
};

export default LoadingSpinner;
